import { useState } from "react";
import useDisclosure from "../hooks/use-disclosure";
import LoadingOverlay from "./LoadingOverlay";
import { PdfGenerator } from "./PdfGenerator";
import PdfViewDrawer from "./PdfViewDrawer";

interface ReportViewerProps {
  children: React.ReactNode;
  pdfRef: React.RefObject<{ generatePdf: () => void }>;
  loading?: boolean;
  onClose?: () => void;
  title?: string;
}

/**
 * ReportViewer component to render a pdf viewer/print
 *
 * @interface ReportViewerProps
 * @param children - The children to render
 * @param pdfRef - The ref to the pdf generator
 *
 * @returns ReportViewer component
 *
 * @author [Rajkumar Rathod]
 */
const ReportViewer = ({
  children,
  pdfRef,
  loading,
  onClose,
  title = "Visit",
}: ReportViewerProps) => {
  const [previewFile, setPreviewFile] = useState<string>("");
  const previewFileDisclosure = useDisclosure(false, { onClose });

  return (
    <>
      {/* Pdf View Drawer */}
      <PdfViewDrawer
        w="65vw"
        file={previewFile}
        disclosureHost={previewFileDisclosure}
        title={title}
      />

      {/* Hidden PDF component with prefilled data */}
      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          setPreviewFile(url);
          previewFileDisclosure.open();
        }}
      >
        {children}
      </PdfGenerator>

      <LoadingOverlay isLoading={!!loading} />
    </>
  );
};

export default ReportViewer;
